import api from '../apiService';
import { API_ROUTES } from '../constants';
import { ActionExecutionDto, ActionScope, PlaybookCreateDto, PlaybookDto, PlaybookExecutionDto, PlaybookTemplate } from '../types';

export type { ActionExecutionDto, ActionScope, EntityRef, PlaybookActionDto, PlaybookCreateDto, PlaybookDto, TargetGroup } from '../types';

const API_BASE_URL = API_ROUTES.PLAYBOOKS;

/** Mirrors `PlaybookController.MAX_RECOMMENDATION_IDS_SIZE`. */
const MAX_RECOMMENDATION_IDS_PER_REQUEST = 100;

export const playbookService = {
  getPlaybooks: async (params?: { isTriggerable?: boolean; entityType?: string; actionType?: string }) => {
    const query = params
      ? Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = query ? `${API_BASE_URL}?${query}` : API_BASE_URL;
    const response = await api.get<PlaybookDto[]>(url);
    return response.data;
  },

  getPlaybookById: async (id: string) => {
    const response = await api.get<PlaybookDto>(`${API_BASE_URL}/${id}`);
    return response.data;
  },

  createPlaybook: async (playbook: PlaybookCreateDto) => {
    const response = await api.post<PlaybookDto>(API_BASE_URL, playbook);
    return response.data;
  },

  updatePlaybook: async (id: string, playbook: Partial<PlaybookDto>) => {
    const response = await api.patch<PlaybookDto>(`${API_BASE_URL}/${id}`, playbook);
    return response.data;
  },

  deletePlaybook: async (id: string) => {
    await api.delete(`${API_BASE_URL}/${id}`);
  },

  // `selectedActions` is only sent when explicitly provided (checked via `!== undefined`, not truthiness,
  // so a deliberate empty array is still forwarded as an envelope body rather than silently downgrading
  // to the legacy shape), so existing callers (e.g. TriggerPlaybookButton.tsx's flat
  // { resource_type, resource_id, entity } body) keep posting the legacy shape the controller's
  // no-`selectedActions`-key branch expects.
  executePlaybook: async (playbookId: string, payload: Record<string, unknown>, selectedActions?: ActionScope[]) => {
    const body = selectedActions !== undefined ? { payload, selectedActions } : payload;
    const response = await api.post<PlaybookExecutionDto | undefined>(`${API_BASE_URL}/${playbookId}/execute`, body);
    return response.data;
  },

  getExecutionActions: async (playbookId: string, executionId: string) => {
    const response = await api.get<ActionExecutionDto[]>(`${API_BASE_URL}/${playbookId}/executions/${executionId}/actions`);
    return response.data;
  },

  // The shared `api` wrapper is fetch-based and has no query-string serialization (an axios-style
  // `{ params }` config is silently dropped), so the repeated `ids` params are built by hand here.
  // Requests are chunked to stay under the endpoint's MAX_RECOMMENDATION_IDS_SIZE, which rejects
  // oversized batches with a 400 rather than truncating them.
  getExecutionsByRecommendationIds: async (ids: string[]) => {
    if (!ids.length) {
      return [];
    }
    const chunks = Array.from({ length: Math.ceil(ids.length / MAX_RECOMMENDATION_IDS_PER_REQUEST) }, (_, index) =>
      ids.slice(index * MAX_RECOMMENDATION_IDS_PER_REQUEST, (index + 1) * MAX_RECOMMENDATION_IDS_PER_REQUEST)
    );
    const responses = await Promise.all(
      chunks.map(chunk => {
        const query = chunk.map(id => `ids=${encodeURIComponent(id)}`).join('&');
        return api.get<PlaybookExecutionDto[]>(`${API_BASE_URL}/executions/by-recommendation?${query}`);
      })
    );
    return responses.flatMap(response => response.data ?? []);
  },

  getExecutionsHistory: async (id: string, page: number = 1, perPage: number = 50) => {
    const response = await api.get<any[]>(`${API_BASE_URL}/${id}/${API_ROUTES.EXECUTION_HISTORY}?page=${page}&per_page=${perPage}`);
    return response.data ?? [];
  },

  getPlaybookAudiLogs: async (playbookId: string) => {
    const response = await api.get(`${API_BASE_URL}/${playbookId}/history`);
    return response.data;
  },

  getPlaybookTemplates: async (): Promise<PlaybookTemplate[]> => {
    const response = await api.get<PlaybookTemplate[]>(`${API_BASE_URL}/templates`);
    return response.data;
  },

  getPlaybookTemplateById: async (templateId: string): Promise<PlaybookTemplate> => {
    const response = await api.get<PlaybookTemplate>(`${API_BASE_URL}/templates/${templateId}`);
    return response.data;
  },
};
